import 'zone.js';
import 'core-js-bundle';
import * as singleSpa from 'single-spa';
import fetch from 'cross-fetch';
import '@angular/localize/init';
import { registerApplicationsWithAuthorization, registerUserAuthApplication } from './apps';
import { initDev } from './apps.dev';
import { initFooter } from './assets/scripts/footer';
import { errorPage } from './assets/scripts/error-page';
import { initAuthTokenChecker } from './assets/scripts/check-auth-token-util';
import { initSessionTimeoutUtil } from './assets/scripts/session-timeout-util';
import { initGlobalInterceptor } from './assets/scripts/interceptors/global-interceptor-util';
import { initAppInsights } from './app-insights';
import { initPolyfills } from './polyfills';

initPolyfills();
errorPage.init();

const rootAppName = 'RootUI';
const urls = {
  projects: window.isLocal ? 'http://localhost:14561' : window.UrlVenueAppProjects,
  users: window.isLocal ? 'http://localhost:1201' : window.UrlVenueAppUsers,
  storage: window.isLocal ? 'http://localhost:1202' : window.UrlVenueAppStorage,
  qna: window.isLocal ? 'http://localhost:1204' : window.UrlVenueAppQnA,
  reporting: window.isLocal ? 'http://localhost:1205' : window.UrlVenueAppReporting,
  dataprotect: window.isLocal ? 'http://localhost:5000/api' : window.UrlDataProtectApi,
  notifications: window.isLocal
    ? 'http://ven-dev-int-notifications.azurewebsites.net'
    : window.UrlVenueAppNotifications,
  dataprivacy: window.isLocal ? 'http://localhost:1206' : window.UrlVenueAppDataPrivacy,
  eca: window.isLocal ? 'http://localhost:1212' : window.UrlVenueAppECA,
  ocr: window.isLocal ? 'http://localhost:14620' : window.UrlVenueAppOCR,
  projectsadmin: window.isLocal ? 'http://localhost:14591' : window.UrlVenueAppAdministration,
  documents: window.isLocal ? 'http://localhost:14611' : window.UrlVenueAppDocuments,
  environment: window.isLocal ? 'http://localhost:1207' : window.UrlVenueAppEnvironment
};
const appInsights = window.isLocal ? null : initAppInsights(urls.environment);

singleSpa.addErrorHandler(err => {
  console.error(err);
  appInsights?.trackException({
    exception: err,
    properties: {
      appName: rootAppName,
      appOrParcelName: err.appOrParcelName,
      appStatus: singleSpa.getAppStatus(err.appOrParcelName)
    }
  });
});

//TODO: Use global variables
const segments = location.pathname.split('/');

let projectId = ``;
if (segments[1] === 'ui') {
  if ((segments[2] === 'p' || segments[2] === 'reportviewer') && segments[3]) {
    projectId = segments[3];
  } else {
    const attrName = 'projectId';
    const regex = new RegExp('[?&]' + attrName + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(window.location.href);
    projectId = results && results[2] ? results[2].replace(/\+/g, ' ') : null;
  }
}

initApplications();

function initApplications() {
  if (isAuthApp()) {
    initAuthApp();
  } else {
    fetchAppsData(
      data => {
        initApps(data.authorizationContext, data.token);
      },
      _ => err => (window.isLocal ? console.log(err) : errorPage.show())
    );
  }
}

function initAuthApp() {
  console.log('auth initializing');
  if (window.isLocal) {
    initDev({}, urls);
  } else {
    registerUserAuthApplication(urls);
    appInsights.trackCurrentPageView();
  }
  console.log('auth initialized');
}

function initApps(authorizationContext, token) {
  if (window.isLocal) {
    console.log(authorizationContext);
  } else {
    initAuthTokenChecker(token.authToken);
    configureAppInsights(authorizationContext.user.id.toString());
  }
  authorizationContext.availablePolicies = getAvailablePolciies(authorizationContext.policies);
  authorizationContext.user.languageId = authorizationContext.language;

  if (window.isLocal) {
    // init apps for dev:
    initGlobalInterceptor();
    initDev(authorizationContext, urls);
    initFooter(authorizationContext);
  } else {
    // init apps for prod:
    initGlobalInterceptor(`Bearer ${token.authToken}`);
    registerApplicationsWithAuthorization(authorizationContext, urls, token);
    initFooter(authorizationContext);
    if (authorizationContext.sessionTimeout) {
      initSessionTimeoutUtil(authorizationContext.sessionTimeout);
    }
  }
  console.log('apps initialized');
}

function isAuthApp() {
  return location.pathname.includes('/auth');
}

function initZendesk(currentUserId, t) {
  if (window.zE) {
    zE('webWidget', 'updateSettings', {
      webWidget: {
        authenticate: {
          jwtFn: function (callback) {
            fetchZendeskToken(callback, currentUserId, 'articles', t);
          }
        },
      }
    });
  }
}

function configureAppInsights(userId) {
  appInsights.setAuthenticatedUserContext(userId);
  appInsights.context.user.id = userId;
  appInsights.trackCurrentPageView();
}

function fetchAppsData(onSuccess, onError) {
  if (window.isLocal) {
    fetchAuthorizationContext(
      undefined,
      authorizationContext => {
        onSuccess({ authorizationContext, token: undefined });
        initZendesk(authorizationContext.user.id.toString(), undefined);
      },
      error => console.error(error)
    );
  } else {
    fetchToken(
      token => {
        if (token.authToken) {
          fetchAuthorizationContext(
            token.authToken,
            authorizationContext => {
              onSuccess({ authorizationContext, token });
              initZendesk(authorizationContext.user.id.toString(), token);
            },
            error => onError && onError(error)
          );
        }
      },
      error => onError && onError(error)
    );
  }
}

function fetchToken(onSuccess, onError) {
  fetch('/ui/GetToken.aspx', {
    credentials: 'include'
  })
    .then(response => response.json())
    .then(onSuccess)
    .catch(onError);
}

function fetchAuthorizationContext(authToken, onSuccess, onError) {
  const url = projectId ? `${urls.projects}/api?projectIdForPolicies=${projectId}` : `${urls.projects}/api`;

   fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'ai-cloud-role': rootAppName,
      Authorization: authToken && `Bearer ${authToken}`
    }
  })
    .then(response => response.json())
    .then(onSuccess)
    .catch(onError);
}

function getAvailablePolciies(policies) {
  return Object.keys(policies).filter(key => {
    if (policies[key]) {
      return key;
    }
  });
}

function fetchZendeskToken(callback, userId, mode, t) {
  const url = urls.users + '/api/env/zendesk/' + userId + '/' + mode;
  fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'ai-cloud-role': rootAppName,
      Authorization: `Bearer ${t.authToken}`
    },
  })
    .then(getResponse)
    .then(response => JSON.parse(response))
    .then(result => callback(result.token))
    .catch(error => {
      console.log(error);
    });
}

function getResponse(res) {
  if(!res || !res.ok)
    throw new Error ('Unexpected error while executing request');

  if (res.type.includes('json')) {
    return res.json().then(x => x);
  }
  
  if (res.type.includes('text')) {
    return res.text();
  }
  else {
    return res.blob().then(x => x.text());
  }
}
